import React, { FC } from 'react';

import { ClockRewindIcon } from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { PopoverClose } from '../../../components/popover/index';
import { StringKey } from '../../../lang/index';
import { EventTypeBackend } from '../../../types/events.types';
import { BlockedTooltipAction } from '../desktop/history/blocked-action-tooltip';
import { manageReverseVestingStore } from './manage-reverse-vesting.store';

export type ManageReverseVestingMenuItemProps = {
  blockedMessageKey?: StringKey;
  disabled?: boolean;
  eventId: string;
  eventType: EventTypeBackend.COMPANY_INCORPORATION | EventTypeBackend.SHARE_ISSUANCE;
  /** Names the modal's subtitle and opens that row first. */
  transactionId: string;
};

/**
 * Manage Reverse Vesting action, shown on each transaction row of Company Incorporation and Share
 * Issuance events — and only there, never on the event's own "⋮" menu, per the AC. Any row opens
 * the same event-scoped modal listing every row.
 *
 * Unlike Edit Details and Delete next to it, this one is never disabled by the valuation-period
 * lock: a locked event opens the same flow with only its schedules editable. A terminated
 * stakeholder's row is settled history, so callers disable it there.
 */
export const ManageReverseVestingMenuItem: FC<ManageReverseVestingMenuItemProps> = ({
  blockedMessageKey,
  disabled = false,
  eventId,
  eventType,
  transactionId,
}) => {
  const open = manageReverseVestingStore(({ open }) => open);

  if (disabled)
    return (
      <BlockedTooltipAction allowed={false} messageKey={blockedMessageKey}>
        <div
          className="flex w-full items-center gap-2 rounded p-[6px] pr-8 text-sm text-gray-400"
          data-permission-required
        >
          <ClockRewindIcon iconColor="#98A2B3" />
          <span className="text-nowrap text-sm font-normal text-gray-400">
            <AppFormattedMessage id="MANAGE_REVERSE_VESTING" />
          </span>
        </div>
      </BlockedTooltipAction>
    );

  return (
    <PopoverClose asChild>
      <div
        className="flex w-full cursor-pointer items-center gap-2 rounded p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
        data-permission-required
        onClick={(clickEvent) => {
          // The event row header toggles its transaction table on click, so the menu must not let
          // the event through to it.
          clickEvent.stopPropagation();
          open({ eventId, eventType, transactionId });
        }}
      >
        <ClockRewindIcon />
        <span className="text-nowrap text-sm font-normal text-gray-700">
          <AppFormattedMessage id="MANAGE_REVERSE_VESTING" />
        </span>
      </div>
    </PopoverClose>
  );
};
